import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import { useFlags } from "launchdarkly-react-client-sdk";

import { usePermissions } from "components/Hook";
import Text from "components/common/Text";
import SearchableAutoComplete from "components/common/AutoComplete";
import CustomChip from "components/common/Chip";
import CustomTooltip from "components/common/Tooltip";
import PageLoader from "components/common/PageLoader";

import { getEmployeesWithClockInStatus } from "api/clockInWorkforce";
import { baseUrl } from "util/APIBaseUrl";
import { showSnackbar } from "store/actions/Utility";
import { AlertType } from "components/common/Snackbar";

import "./WorkforceClockIn.scss";

const STATUS_CONFIG = {
  clocked_in: { color: "#06B500", label: "Available" },
  on_break: { color: "#EB730E", label: "On break" },
  clocked_out: { color: "#939393", label: "Out of office" },
  on_leave: { color: "#939393", label: "On leave" },
  not_activated: { color: "#939393", label: "Clock in not enabled" },
  not_clocked_in: { color: "#939393", label: "Not clocked in" },
  no_shift: { color: "#939393", label: "Shift not selected" },
};

const AVAILABILITY_OPTIONS = [
  { value: "clocked_in", label: "Available" },
  { value: "on_break", label: "On break" },
  { value: "clocked_out", label: "Out of office" },
];

const SORT_OPTIONS = [
  { value: "name_asc", label: "Alphabetical order (A-Z)" },
  { value: "joining_desc", label: "Joining date" },
];

const ITEMS_PER_PAGE = 10;

function WorkforceClockIn() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loggedInUser } = usePermissions();
  const { temporaryReplacement } = useFlags();
  const customerId = loggedInUser?.customerId;

  const [employees, setEmployees] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [page] = useState(0);

  const fetchEmployees = useCallback(async () => {
    if (!customerId) return;
    try {
      setLoading(true);
      const response = await getEmployeesWithClockInStatus(customerId, {
        page: page + 1,
        limit: ITEMS_PER_PAGE,
      });

      let rows = [];
      let count = 0;
      if (response?.success && response?.data) {
        rows = response.data?.rows || [];
        count = response.data?.count || 0;
      } else if (response?.rows) {
        rows = response.rows;
        count = response.count || 0;
      }

      setEmployees(rows);
      setTotalCount(count);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching workforce clock-in status:", error);
      dispatch(
        showSnackbar({
          type: AlertType.error,
          message: error?.message || "Failed to load workforce data",
          dispatch,
        })
      );
    } finally {
      setLoading(false);
    }
  }, [customerId, dispatch, page]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const filteredEmployees = useMemo(() => {
    let result = employees;

    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter((emp) => {
        const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
        return fullName.includes(searchLower);
      });
    }

    if (availabilityFilter) {
      result = result.filter((emp) => emp.clockInStatus?.status === availabilityFilter);
    }

    switch (sortBy) {
      case "name_asc":
        result = [...result].sort((a, b) =>
          `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
        );
        break;
      case "joining_desc":
        result = [...result].sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
        break;
      default:
        break;
    }

    return result;
  }, [employees, search, availabilityFilter, sortBy]);

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearch(value);
  };

  const handleAvailabilityChange = (value) => {
    setAvailabilityFilter(value);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
  };

  const handleCardClick = (employeeId) => {
    navigate(`/main/details/${employeeId}`);
  };

  const getStatusColor = (status) => {
    return STATUS_CONFIG[status]?.color || "transparent";
  };

  if (loading && employees.length === 0) {
    return <PageLoader />;
  }

  return (
    <div className="workforce-clockin">
      {loading && <PageLoader />}
      <div className="workforce-clockin__filters">
        <div className="workforce-clockin__filter-field">
          <Text
            name="workforceSearch"
            placeholder="Search employee by name"
            value={search}
            onChange={handleSearchChange}
            size="small"
            startIcon={<SearchIcon className="workforce-clockin__search-icon" />}
          />
        </div>
        <div className="workforce-clockin__filter-field">
          <SearchableAutoComplete
            name="availabilityFilter"
            label=""
            placeholder="Availability"
            options={AVAILABILITY_OPTIONS}
            value={availabilityFilter}
            onChange={handleAvailabilityChange}
          />
        </div>
        <div className="workforce-clockin__filter-field">
          <SearchableAutoComplete
            name="sortFilter"
            label=""
            placeholder="Sort"
            options={SORT_OPTIONS}
            value={sortBy}
            onChange={handleSortChange}
          />
        </div>
      </div>

      <p className="heading-h5-medium workforce-clockin__total">Total Employees ({totalCount})</p>

      <div className="workforce-clockin__grid">
        {filteredEmployees.map((employee) => (
          <div
            key={employee.employeeId}
            className="workforce-clockin__card"
            onClick={() => handleCardClick(employee.employeeId)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleCardClick(employee.employeeId)}
          >
            <div className="workforce-clockin__card-profile">
              {temporaryReplacement && employee.employeeStatus === "Substitute" && (
                <div className="workforce-clockin__card-tag">
                  <CustomTooltip content={employee.substituteMessage || "Substitute"} direction="bottom">
                    <div>
                      <CustomChip label="Substitute" size="small" />
                    </div>
                  </CustomTooltip>
                </div>
              )}
              <div className="workforce-clockin__avatar-wrapper">
                <img
                  className="workforce-clockin__avatar-img"
                  src={
                    employee.pictureVersion
                      ? `${baseUrl}/employee/${employee.employeeId}/logo?id=${employee.pictureVersion}`
                      : ""
                  }
                  alt={`${employee.firstName} ${employee.lastName}`}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <CustomTooltip
                  content={
                    employee.clockInStatus?.statusMessage || STATUS_CONFIG[employee.clockInStatus?.status]?.label || ""
                  }
                  direction="bottom"
                >
                  <div
                    className="workforce-clockin__status-dot"
                    style={{ backgroundColor: getStatusColor(employee.clockInStatus?.status) }}
                  />
                </CustomTooltip>
              </div>
            </div>

            <div className="workforce-clockin__card-info">
              <p className="heading-h5-medium workforce-clockin__card-name">
                {employee.firstName} {employee.lastName}
              </p>
              <p className="para-body-s-medium workforce-clockin__card-title">{employee.jobTitle}</p>
            </div>

            {employee.clockInStatus?.statusMessage && (
              <div className="workforce-clockin__card-status-msg">
                <p className="para-body-xs-regular workforce-clockin__card-status-text">
                  {employee.clockInStatus.statusMessage}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredEmployees.length === 0 && !loading && (
        <div className="workforce-clockin__empty">
          <p className="para-body-m-regular">No employees found</p>
        </div>
      )}
    </div>
  );
}

export default WorkforceClockIn;
